import { FunctionComponent, useMemo } from "react";

import { listSelector } from "@/pages/Private/redux/list/list.slice";
import { useAppSelector } from "@/redux/hooks";
import { ListSourceType } from "@/enum/list.enum";

import { ListPerson } from "../../../List/schema/list";
import { isDummyProspect } from "../../../List/utils/columns/dummyProspect";
import { renderCurrentScoreResult } from "../../../List/utils/allColumns";
import { Tabs } from "../../../List/components/CampaignOverview/components/Lead/Lead";
import { LeadName } from "../../../List/components/CampaignOverview/components/Lead/components/LeadName";
import { JobTitle } from "../../../List/components/CampaignOverview/components/Lead/components/JobTitle";
import { OrgName } from "../../../List/components/CampaignOverview/components/Lead/components/OrgName";
import { SenderInfo } from "../../../List/components/CampaignOverview/components/Lead/components/SenderInfo";
import { LeadColorDots } from "../../../List/components/CampaignOverview/components/Lead/components/LeadColorDots";
import { LeadColorLabels } from "../../../List/components/CampaignOverview/components/Lead/components/LeadColorLabels";
import { DeepResearchStatusChip } from "../../../List/components/CampaignOverview/components/Lead/components/DeepResearchStatusChip";
import { ReviewCampaignTag } from "../../../List/components/CampaignOverview/components/Lead/components/ReviewCampaignTag";
import { ChatDraftsTag } from "../../../List/components/CampaignOverview/components/Lead/components/ChatDraftsTag";
import { ReviewLeadActions } from "../../../List/components/CampaignOverview/components/Lead/components/ReviewLeadActions";
import { useDeepResearchStatus } from "../../../List/components/CampaignOverview/components/Lead/utils/useDeepResearchStatus";
import { resolveLeadCampaignConfig } from "../../../List/components/CampaignOverview/components/Lead/utils/resolveLeadCampaignConfig";
import {
	getAiSnippetStatus,
	getCampaignAiSnippetColumnIds,
} from "../../../List/components/CampaignOverview/components/Lead/utils/getAiSnippetStatus";
import { LeadCardCompact } from "./LeadCardCompact";
import { getCardProvenance, getLeadScore } from "./lead-card-compact.helper";

interface ReviewLeadCompactProps {
	row: ListPerson;
	handleSetCurrentLead: (index: number) => void;
	handleTabsChange?: (tab: Tabs) => void;
	onAssignClick: () => void;
	onIgnoreClick: () => void;
	isCollapsed?: boolean;
}

/**
 * A lead waiting in Review to send, on the Inbox/Outbox compact card (PREC-4175): score, sender
 * and color dots in the gutter; name, title and company stacked; the list it came from; the
 * campaign it will go out with, as the tag that changes it, beside its AI-snippet state and any
 * chat drafts.
 * The "Ignore lead" / assign actions hang under the card on hover, exactly as on the list-row
 * card it replaces — both render the same extracted pieces.
 */
export const ReviewLeadCompact: FunctionComponent<ReviewLeadCompactProps> = ({
	row,
	handleSetCurrentLead,
	handleTabsChange,
	onAssignClick,
	onIgnoreClick,
	isCollapsed = false,
}) => {
	const { inbox, currentLeadIndex } = useAppSelector(listSelector);
	const rowIndex = inbox.indexOf(row);

	const selectedCampaignConfig = useMemo(() => resolveLeadCampaignConfig(row), [row]);
	const aiSnippetStatus = useMemo(() => {
		if (!selectedCampaignConfig) {
			return null;
		}

		return getAiSnippetStatus(
			row,
			getCampaignAiSnippetColumnIds(selectedCampaignConfig.initialNodes, row.list?.columns)
		);
	}, [selectedCampaignConfig, row]);
	const deepResearchStatus = useDeepResearchStatus(row);
	const provenance = getCardProvenance(row);
	const isTrackerList = row.list?.sourceType === ListSourceType.TRACKER;

	const scoreBadge = renderCurrentScoreResult({
		scoring: row.prospect?.scorePersonaCategoryResult,
		score: getLeadScore(row),
	});
	const sender = selectedCampaignConfig ? (
		<SenderInfo row={row} selectedCampaignConfig={selectedCampaignConfig} />
	) : null;

	const openChat = () => {
		handleSetCurrentLead(rowIndex);
		handleTabsChange?.(Tabs.CHAT);
	};

	if (isCollapsed) {
		return (
			<div className="flex flex-col items-center gap-1 py-2 w-full border-b border-border-light">
				{scoreBadge}
				{sender}
			</div>
		);
	}

	return (
		<div className="group/leadcard relative z-0 hover:z-50">
			<LeadCardCompact
				chips={
					<>
						<ReviewCampaignTag
							aiSnippetStatus={aiSnippetStatus}
							allowCampaignChange
							isTrackerList={isTrackerList}
							row={row}
							selectedCampaignConfig={selectedCampaignConfig}
							onAssignClick={onAssignClick}
						/>
						<ChatDraftsTag row={row} onReviewChat={() => handleTabsChange?.(Tabs.CHAT)} />
						{deepResearchStatus && <DeepResearchStatusChip compact status={deepResearchStatus} />}
					</>
				}
				company={<OrgName compact row={row} />}
				gutter={
					<>
						{scoreBadge}
						{sender}
						<LeadColorDots row={row} onReviewChat={openChat} />
					</>
				}
				isActive={currentLeadIndex === rowIndex}
				name={<LeadName row={row} />}
				// The campaign is named by the tag below, with its change action; the row names the list.
				provenance={{ listName: provenance.listName, listTo: provenance.listTo }}
				title={<JobTitle compact row={row} />}
				topLabels={<LeadColorLabels row={row} />}
				onClick={() => handleSetCurrentLead(rowIndex)}
			/>
			<ReviewLeadActions
				isFindLeadRow={isDummyProspect(row)}
				isTrackerList={isTrackerList}
				row={row}
				onAssignClick={onAssignClick}
				onBeforeAssign={() => handleSetCurrentLead(rowIndex)}
				onIgnoreClick={onIgnoreClick}
			/>
		</div>
	);
};
